import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trash2, Mail, Phone, Building2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "closed"];
const badgeColor = { new: "bg-blue-100 text-blue-700", contacted: "bg-amber-100 text-amber-700", closed: "bg-slate-200 text-slate-600" };

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/leads").then((r) => setLeads(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    toast.success("Lead updated");
  };
  const remove = async (id) => {
    await api.delete(`/admin/leads/${id}`);
    setLeads((l) => l.filter((x) => x.id !== id));
    toast.success("Lead deleted");
  };

  return (
    <div data-testid="admin-leads-view">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-navy">Quote Requests</h2>
          <p className="text-slate-500 text-sm mt-1">{leads.length} total lead{leads.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={load} data-testid="admin-leads-refresh" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : leads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">No leads yet.</div>
      ) : (
        <div className="space-y-4">
          {leads.map((l) => (
            <div key={l.id} data-testid={`lead-card-${l.id}`} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-navy text-lg">{l.name}</h3>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${badgeColor[l.status] || badgeColor.new}`}>{l.status}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                    {l.business_name && <span className="inline-flex items-center gap-1.5"><Building2 size={13} /> {l.business_name}</span>}
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-brand-blue"><Mail size={13} /> {l.email}</a>
                    {l.phone && <span className="inline-flex items-center gap-1.5"><Phone size={13} /> {l.phone}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm capitalize">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`} className="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400">Service:</span> <span className="text-slate-700">{l.service || "—"}</span></div>
                <div><span className="text-slate-400">Preferred contact:</span> <span className="text-slate-700">{l.contact_method}</span></div>
              </div>
              <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600 leading-relaxed">{l.description}</p>
              <p className="mt-2 text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
